"use client";

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Container } from "@/components/layout/container";
import { getProjects, type Project } from "@/api/projects";
import { useScrollTriggerRefresh } from "@/lib/use-scroll-trigger-refresh";
import { optimizedImage } from "@/lib/optimized-image";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

function pickRandom<T>(items: T[], count: number): T[] {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}

export function MoreWork({
  currentSlug,
  initialProjects,
}: {
  currentSlug: string;
  initialProjects?: Project[];
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: getProjects,
    initialData: initialProjects,
  });
  const pool = isLoading ? null : (data ?? []);

  // Seed a deterministic first-three for the server render so the internal links exist in the
  // initial HTML (crawlable); the effect below re-shuffles them on the client after hydration.
  const [others, setOthers] = useState<Project[] | null>(
    initialProjects ? initialProjects.filter((p) => p.slug !== currentSlug).slice(0, 3) : null
  );

  useEffect(() => {
    if (!pool) return;
    setOthers(pickRandom(pool.filter((p) => p.slug !== currentSlug), 3));
  }, [pool, currentSlug]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !others || others.length === 0) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-card]",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: { trigger: root, start: "top 85%", end: "top 55%", scrub: true },
        }
      );
    }, root);

    return () => ctx.revert();
  }, [others]);

  useScrollTriggerRefresh([others]);

  // Distinguish "still loading" (pool hasn't resolved yet, others is null) from "genuinely
  // nothing to show" (pool resolved but there are zero other projects) — only the latter hides
  // the section; the former shows a skeleton so it doesn't silently pop in with a layout shift.
  const stillLoading = pool === null;
  if (!stillLoading && others !== null && others.length === 0) return null;

  return (
    <section ref={rootRef} className="relative py-10 sm:py-[7vh]">
      <Container>
        <span className="font-mono-tech text-xs uppercase tracking-[0.14em] text-[var(--color-text-tertiary)]">
          More work
        </span>
        <div className="mt-6 grid items-start gap-6 sm:grid-cols-3">
          {stillLoading || others === null
            ? Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                  <div className="h-48 w-full flex-shrink-0 animate-pulse bg-[var(--color-bg)]" />
                  <div className="flex flex-grow flex-col p-5">
                    <div className="h-5 w-3/4 animate-pulse rounded bg-[var(--color-bg)]" />
                    <div className="mt-auto h-3.5 w-1/2 animate-pulse rounded bg-[var(--color-bg)]" />
                  </div>
                </div>
              ))
            : others.map((o) => (
            <Link
              key={o.slug}
              href={`/portfolio/${o.slug}`}
              data-card
              className="group flex w-full flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] transition-colors hover:border-[var(--color-primary)]"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizedImage(o.image, 640)}
                alt={`${o.name} interface`}
                className="block h-auto w-full"
                loading="lazy"
              />
              <div className="flex flex-col p-5">
                <h3 className="font-display text-base font-bold leading-tight text-[var(--color-text-primary)]">{o.name}</h3>
                <p className="mt-1.5 text-sm text-[var(--color-text-tertiary)]">{o.category}</p>
              </div>
            </Link>
          ))}
        </div>
      </Container>
    </section>
  );
}
